import axios from '../lib/axios';
import { AxiosError } from 'axios';
import { tokenStorage } from '../utils/tokenStorage';
import { logger } from '../utils/logger';

export interface MembershipTier {
  _id: string;
  clientId: string;
  name: string;
  description: string;
  level: number;
  minPoints: number;
  maxPoints: number;
  benefits: string[];
  pointsMultiplier: number;
  color: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  isHighestTier?: boolean;
  id: string;
}

export interface MembershipInfo {
  preferences: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    pushNotifications: boolean;
  };
  _id: string;
  userId: string;
  email: string;
  fullName: string;
  phoneNumber: string;
  status: string;
  currentTier: MembershipTier;
  pointsBalance: number;
  totalPointsEarned: number;
  totalPointsRedeemed: number;
  referralCode: string;
  referredBy: string | null;
  referralBonusEarned: number;
  totalReferrals: number;
  joinDate: string;
  lastActivityDate: string;
  createdAt: string;
  updatedAt: string;
  membershipNumber: string;
  __v: number;
  membershipAge: number;
  isExpired: boolean;
  id: string;
}

export interface MembershipResponse {
  success: boolean;
  data?: MembershipInfo;
  message?: string;
  error?: string;
}

export interface MembershipTiersResponse {
  success: boolean;
  data?: MembershipTier[];
  message?: string;
  error?: string;
}

// Helper function to get headers with authentication
const getHeaders = async () => {
  const accessToken = tokenStorage.getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  };
};

// Single-flight + caching + failure cooldown for profile API
let inFlightProfilePromise: Promise<import('axios').AxiosResponse<ProfileAPIResponse>> | null =
  null;
let cachedProfileResponse: import('axios').AxiosResponse<ProfileAPIResponse> | null = null;
let lastProfileFetchTime = 0;
let lastProfileErrorTime = 0;
const PROFILE_CACHE_TTL = 30_000; // 30s cache TTL
const FAILURE_COOLDOWN_MS = 15_000; // 15s cooldown after failure to prevent spamming

export const membershipService = {
  clearProfileCache(): void {
    inFlightProfilePromise = null;
    cachedProfileResponse = null;
    lastProfileFetchTime = 0;
    lastProfileErrorTime = 0;
  },

  // Centralized profile fetch with single-flight and caching
  async getProfile(force = false): Promise<import('axios').AxiosResponse<ProfileAPIResponse>> {
    if (force) {
      this.clearProfileCache();
    }

    // Use cache if fresh and not forcing a refresh
    if (!force && cachedProfileResponse && Date.now() - lastProfileFetchTime < PROFILE_CACHE_TTL) {
      return cachedProfileResponse;
    }

    // Respect failure cooldown if not forcing
    if (!force && lastProfileErrorTime && Date.now() - lastProfileErrorTime < FAILURE_COOLDOWN_MS) {
      const err = new Error('Profile fetch temporarily paused due to recent errors');
      (err as Error & { isCooldown?: boolean }).isCooldown = true;
      throw err;
    }

    // Reuse in-flight request if present
    if (inFlightProfilePromise) {
      return inFlightProfilePromise;
    }

    const fetch = async () => {
      const headers = await getHeaders();
      return axios.get('/api/v1/users/profile', { headers });
    };

    inFlightProfilePromise = fetch()
      .then((response) => {
        cachedProfileResponse = response;
        lastProfileFetchTime = Date.now();
        lastProfileErrorTime = 0;
        return response;
      })
      .catch((error) => {
        lastProfileErrorTime = Date.now();
        throw error;
      })
      .finally(() => {
        inFlightProfilePromise = null;
      });

    return inFlightProfilePromise;
  },

  // Get user's membership information from profile API
  getMembershipInfo: async (): Promise<MembershipResponse> => {
    try {
      logger.log('Fetching membership info from profile API...');

      const response = await membershipService.getProfile();

      logger.log('Profile API response:', response.data);

      // Check if membership data exists in profile response
      if (response.data?.success) {
        const user = response.data.user;
        const membership = response.data.membership;

        // If membership data exists in response, return it with isHighestTier fix
        if (membership) {
          // Fix the isHighestTier issue - convert empty object to boolean
          if (membership.currentTier && typeof membership.currentTier.isHighestTier === 'object') {
            membership.currentTier.isHighestTier = false; // Use API provided value or default to false
          }

          logger.log('✅ Membership info successfully extracted from profile API');
          return {
            success: true,
            data: membership,
          };
        }

        // If membership data exists in user object, return it
        if (user?.membership) {
          // Fix the isHighestTier issue - convert empty object to boolean
          if (
            user.membership.currentTier &&
            typeof user.membership.currentTier.isHighestTier === 'object'
          ) {
            user.membership.currentTier.isHighestTier = false; // Use API provided value or default to false
          }

          logger.log('✅ Membership info found in user object');
          return {
            success: true,
            data: user.membership,
          };
        }

        // If no membership data found, return error
        logger.warn('⚠️ No membership data found in API response');
        return {
          success: false,
          error: 'Membership data not available',
        };
      }

      throw new Error('No valid data found in profile response');
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Failed to fetch membership info from profile API:', axiosError);

      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.message ||
          'Failed to fetch membership information',
      };
    }
  },

  // Get membership tiers - simplified to work only with actual API data
  getMembershipTiers: async (): Promise<MembershipTiersResponse> => {
    try {
      logger.log('Fetching membership tiers from profile API...');

      // Get profile data which contains membership info (single-flight/cached)
      const response = await membershipService.getProfile();

      logger.log('Profile API response for tiers:', response.data);

      // Check if we have membership data with currentTier
      if (response.data?.success && response.data?.membership?.currentTier) {
        const currentTier = response.data.membership.currentTier;

        // Fix the isHighestTier issue if it's an empty object
        if (typeof currentTier.isHighestTier === 'object') {
          currentTier.isHighestTier = false; // Set to false by default, backend should provide correct value
        }

        // Return only the current tier as a single-item array
        // This is the most accurate approach since we only have data for the user's current tier
        const tiers: MembershipTier[] = [currentTier];

        logger.log('✅ Membership tier successfully extracted from profile API:', currentTier.name);
        return {
          success: true,
          data: tiers,
        };
      }

      // If no membership data, return error with clear message
      logger.warn('⚠️ No membership data found in profile response');
      return {
        success: false,
        error: 'Membership tier data not available',
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Failed to fetch membership tiers from profile API:', axiosError);

      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.message ||
          'Failed to fetch membership tiers',
      };
    }
  },

  // Calculate points needed to reach next tier
  getPointsToNextTier: (currentPoints: number, allTiers: MembershipTier[]): number => {
    // If no tiers available, return 0
    if (!allTiers || allTiers.length === 0) {
      return 0;
    }

    // If only one tier (current tier), we can't calculate next tier
    if (allTiers.length === 1) {
      const currentTier = allTiers[0];
      // If this tier has maxPoints, calculate points needed to reach that max
      if (currentTier.maxPoints && currentTier.maxPoints > currentPoints) {
        return currentTier.maxPoints - currentPoints;
      }
      // If no maxPoints or already at max, return 0 (highest tier)
      return 0;
    }

    // Original logic for multiple tiers (kept for backward compatibility)
    const sortedTiers = [...allTiers].sort((a, b) => a.minPoints - b.minPoints);
    const currentTierIndex = sortedTiers.findIndex(
      (tier) => currentPoints >= tier.minPoints && currentPoints <= (tier.maxPoints || Infinity),
    );

    if (currentTierIndex === -1 || currentTierIndex === sortedTiers.length - 1) {
      return 0; // Already at highest tier or tier not found
    }

    const nextTier = sortedTiers[currentTierIndex + 1];
    return Math.max(0, nextTier.minPoints - currentPoints);
  },

  // Calculate progress percentage towards next tier
  getProgressPercentage: (currentPoints: number, allTiers: MembershipTier[]): number => {
    // If no tiers available, return 0
    if (!allTiers || allTiers.length === 0) {
      return 0;
    }

    // If only one tier (current tier), calculate progress within that tier
    if (allTiers.length === 1) {
      const currentTier = allTiers[0];
      if (currentTier.maxPoints && currentTier.minPoints !== undefined) {
        const tierRange = currentTier.maxPoints - currentTier.minPoints;
        const progressInTier = currentPoints - currentTier.minPoints;
        return Math.min(100, Math.max(0, (progressInTier / tierRange) * 100));
      }
      // If no range available, assume 100% (at highest tier)
      return 100;
    }

    // Original logic for multiple tiers (kept for backward compatibility)
    const sortedTiers = [...allTiers].sort((a, b) => a.minPoints - b.minPoints);
    const currentTierIndex = sortedTiers.findIndex(
      (tier) => currentPoints >= tier.minPoints && currentPoints <= (tier.maxPoints || Infinity),
    );

    if (currentTierIndex === -1) {
      return 0; // Tier not found
    }

    if (currentTierIndex === sortedTiers.length - 1) {
      return 100; // Already at highest tier
    }

    const currentTier = sortedTiers[currentTierIndex];
    const nextTier = sortedTiers[currentTierIndex + 1];
    const tierRange = nextTier.minPoints - currentTier.minPoints;
    const progressInTier = currentPoints - currentTier.minPoints;

    return Math.min(100, Math.max(0, (progressInTier / tierRange) * 100));
  },
};

export default membershipService;

export interface ProfileAPIResponse {
  success: boolean;
  user?: { membership?: MembershipInfo; [key: string]: unknown };
  membership?: MembershipInfo;
  message?: string;
  error?: string;
}
